import React from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { PDFViewer } from "@react-pdf/renderer";
import { Page, Text, View, Document, StyleSheet } from "@react-pdf/renderer";
import { Translate, I18n } from "react-redux-i18n";
import { FaArrowLeft, FaReceipt } from "react-icons/fa";
import alertify from "alertifyjs";
import MiniLoader from "../components/MiniLoader";
import * as actions from "../redux/actions/ordersActions";
import { formatReceiptNumber } from "../assets/orderUtils";
import "../components/styles/Alertify.css";
import "../components/styles/AlertifyTheme.css";
import "../components/styles/Alertify.min.css";
import "../components/styles/AlertifyTheme.min.css";
import "./styles/OrderPdf.css";

const pdfStyles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
    fontSize: 10,
    color: "#212529",
    paddingTop: 0,
    paddingBottom: 36,
  },
  accentBar: {
    height: 6,
    backgroundColor: "#1db7e2",
    marginBottom: 28,
  },
  content: {
    paddingHorizontal: 42,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 22,
  },
  brandBlock: {
    flexDirection: "column",
    gap: 4,
  },
  brand: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#1db7e2",
    letterSpacing: 1.2,
  },
  brandTagline: {
    fontSize: 8.5,
    color: "#6c757d",
    letterSpacing: 0.4,
  },
  companyBlock: {
    textAlign: "right",
    fontSize: 8.5,
    lineHeight: 1.55,
    color: "#6c757d",
  },
  invoiceTitleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 18,
  },
  invoiceTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#212529",
  },
  invoiceRef: {
    fontSize: 9,
    color: "#6c757d",
    marginTop: 4,
  },
  statusBadge: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 999,
    backgroundColor: "#e8f7fb",
    color: "#0a9ab8",
    fontSize: 8.5,
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  cardsRow: {
    flexDirection: "row",
    gap: 14,
    marginBottom: 22,
  },
  card: {
    flex: 1,
    padding: 14,
    borderRadius: 10,
    backgroundColor: "#f8f9fa",
    borderWidth: 1,
    borderColor: "#e8eaed",
  },
  cardTitle: {
    fontSize: 8,
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: 0.6,
    color: "#6c757d",
    marginBottom: 8,
  },
  cardLine: {
    fontSize: 9.5,
    lineHeight: 1.55,
    color: "#343a40",
  },
  metaRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
  },
  metaLabel: {
    fontSize: 9,
    color: "#6c757d",
  },
  metaValue: {
    fontSize: 9,
    color: "#212529",
    fontWeight: "bold",
  },
  table: {
    borderWidth: 1,
    borderColor: "#e8eaed",
    borderRadius: 10,
    overflow: "hidden",
    marginBottom: 20,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#1db7e2",
    paddingVertical: 9,
    paddingHorizontal: 12,
  },
  tableHeaderText: {
    color: "#ffffff",
    fontSize: 8.5,
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 12,
    paddingHorizontal: 12,
    backgroundColor: "#ffffff",
    borderTopWidth: 1,
    borderTopColor: "#e8eaed",
  },
  colDetail: { width: "58%" },
  colQty: { width: "14%", textAlign: "center" },
  colPrice: { width: "28%", textAlign: "right" },
  bottomRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 16,
  },
  notesBox: {
    flex: 1,
    padding: 14,
    borderRadius: 10,
    backgroundColor: "#f8f9fa",
    borderWidth: 1,
    borderColor: "#e8eaed",
  },
  notesTitle: {
    fontSize: 8.5,
    fontWeight: "bold",
    marginBottom: 6,
    color: "#212529",
  },
  notesText: {
    fontSize: 8.5,
    lineHeight: 1.55,
    color: "#6c757d",
  },
  totalsBox: {
    width: 180,
    padding: 14,
    borderRadius: 10,
    backgroundColor: "#f4fbfe",
    borderWidth: 1,
    borderColor: "#b8e8f4",
  },
  totalsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 6,
    fontSize: 9.5,
    color: "#495057",
  },
  totalsRowStrong: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#b8e8f4",
    fontSize: 12,
    fontWeight: "bold",
    color: "#0a9ab8",
  },
  footer: {
    marginTop: 24,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "#e8eaed",
    textAlign: "center",
    fontSize: 8,
    color: "#adb5bd",
  },
});

function getInvoiceLabels() {
  return {
    invoice: I18n.t("order_invoice"),
    billTo: I18n.t("order_bill_to"),
    invoiceDate: I18n.t("order_invoice_date"),
    orderNumber: I18n.t("order_number"),
    paymentMethod: I18n.t("order_payment_method"),
    paymentValue: I18n.t("order_payment_card"),
    detail: I18n.t("order_detail"),
    quantity: I18n.t("order_quantity"),
    price: I18n.t("order_price"),
    subtotal: I18n.t("order_subtotal"),
    customerNotes: I18n.t("order_customer_notes"),
    notesLine1: I18n.t("order_notes_line1"),
    notesLine2: I18n.t("order_notes_line2"),
    footer: I18n.t("order_invoice_footer"),
    kind: I18n.t("order_kind"),
  };
}

function OrderDocument({ orderItem, labels }) {
  const billToLines = [
    orderItem.Name,
    orderItem.Organization,
    orderItem.Address,
    orderItem.Location,
  ].filter(Boolean);
  const receiptNumber = formatReceiptNumber(orderItem);

  return (
    <Document author="Godiway" title={`${labels.invoice} ${receiptNumber}`}>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.accentBar} />

        <View style={pdfStyles.content}>
          <View style={pdfStyles.header}>
            <View style={pdfStyles.brandBlock}>
              <Text style={pdfStyles.brand}>GODIWAY</Text>
              <Text style={pdfStyles.brandTagline}>www.godiway.com</Text>
            </View>
            <View style={pdfStyles.companyBlock}>
              <Text>GODIWAY, LLC</Text>
              <Text>2815 DIRECTORS ROW, STE 100</Text>
              <Text>ORLANDO, FL 32809, USA</Text>
            </View>
          </View>

          <View style={pdfStyles.invoiceTitleRow}>
            <View>
              <Text style={pdfStyles.invoiceTitle}>{labels.invoice}</Text>
              <Text style={pdfStyles.invoiceRef}>{receiptNumber}</Text>
            </View>
            <Text style={pdfStyles.statusBadge}>{orderItem.Status}</Text>
          </View>

          <View style={pdfStyles.cardsRow}>
            <View style={pdfStyles.card}>
              <Text style={pdfStyles.cardTitle}>{labels.billTo}</Text>
              {billToLines.map((line) => (
                <Text key={line} style={pdfStyles.cardLine}>
                  {line}
                </Text>
              ))}
            </View>

            <View style={pdfStyles.card}>
              <Text style={pdfStyles.cardTitle}>{labels.invoice}</Text>
              <View style={pdfStyles.metaRow}>
                <Text style={pdfStyles.metaLabel}>{labels.invoiceDate}</Text>
                <Text style={pdfStyles.metaValue}>{orderItem.CreatedAt}</Text>
              </View>
              <View style={pdfStyles.metaRow}>
                <Text style={pdfStyles.metaLabel}>{labels.orderNumber}</Text>
                <Text style={pdfStyles.metaValue}>{receiptNumber}</Text>
              </View>
              <View style={pdfStyles.metaRow}>
                <Text style={pdfStyles.metaLabel}>{labels.paymentMethod}</Text>
                <Text style={pdfStyles.metaValue}>{labels.paymentValue}</Text>
              </View>
              {orderItem.Kind ? (
                <View style={pdfStyles.metaRow}>
                  <Text style={pdfStyles.metaLabel}>{labels.kind}</Text>
                  <Text style={pdfStyles.metaValue}>{orderItem.Kind}</Text>
                </View>
              ) : null}
            </View>
          </View>

          <View style={pdfStyles.table}>
            <View style={pdfStyles.tableHeader}>
              <Text style={[pdfStyles.tableHeaderText, pdfStyles.colDetail]}>
                {labels.detail}
              </Text>
              <Text style={[pdfStyles.tableHeaderText, pdfStyles.colQty]}>
                {labels.quantity}
              </Text>
              <Text style={[pdfStyles.tableHeaderText, pdfStyles.colPrice]}>
                {labels.price}
              </Text>
            </View>
            <View style={pdfStyles.tableRow}>
              <Text style={pdfStyles.colDetail}>{orderItem.Plan}</Text>
              <Text style={pdfStyles.colQty}>1</Text>
              <Text style={pdfStyles.colPrice}>${orderItem.Total}</Text>
            </View>
          </View>

          <View style={pdfStyles.bottomRow}>
            <View style={pdfStyles.notesBox}>
              <Text style={pdfStyles.notesTitle}>{labels.customerNotes}</Text>
              <Text style={pdfStyles.notesText}>{labels.notesLine1}</Text>
              <Text style={pdfStyles.notesText}>{labels.notesLine2}</Text>
            </View>
            <View style={pdfStyles.totalsBox}>
              <View style={pdfStyles.totalsRow}>
                <Text>{labels.subtotal}</Text>
                <Text>${orderItem.Total}</Text>
              </View>
              <View style={pdfStyles.totalsRowStrong}>
                <Text>{I18n.t("order_total")}</Text>
                <Text>${orderItem.Total}</Text>
              </View>
            </View>
          </View>

          <Text style={pdfStyles.footer}>{labels.footer}</Text>
        </View>
      </Page>
    </Document>
  );
}

function getStatusClass(status) {
  const value = String(status || "").toLowerCase();
  if (/(paid|payed|complete|success|approved)/i.test(value)) {
    return "order-view__status--success";
  }
  if (/(pending|process|waiting)/i.test(value)) {
    return "order-view__status--pending";
  }
  return "order-view__status--default";
}

class OrderPdf extends React.Component {
  state = {
    loading: true,
    error: "",
    orderItem: null,
  };

  componentDidMount() {
    this.fetchOrder(this.props.match.params.order_id);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.order_id !== this.props.match.params.order_id) {
      this.fetchOrder(this.props.match.params.order_id);
    }
  }

  fetchOrder = async (order_id) => {
    if (!order_id) {
      this.setState({ loading: false, error: "try_later", orderItem: null });
      return;
    }

    if (!this.props.token || !this.props.user_id) {
      this.setState({ loading: false, error: "warning_login", orderItem: null });
      return;
    }

    this.setState({ loading: true, error: "", orderItem: null });

    try {
      const orderItem = await this.props.LoadItem(this.props.token, {
        id: this.props.user_id,
        order_id,
        lang: I18n.t("locale"),
      });

      if (!orderItem?.Id) {
        throw "empty";
      }

      this.setState({
        loading: false,
        error: "",
        orderItem,
      });
    } catch (error) {
      const message = error || "try_later";
      alertify.warning(I18n.t(message));
      this.setState({
        loading: false,
        error: message,
        orderItem: null,
      });
    }
  };

  renderHeader(orderItem) {
    const receiptNumber = formatReceiptNumber(orderItem);

    return (
      <header className="order-view__header">
        <div className="order-view__header-top">
          <Link to="/paylist" className="order-view__back">
            <FaArrowLeft />
            <Translate value="pay_orders" />
          </Link>
          <span className={`order-view__status ${getStatusClass(orderItem.Status)}`}>
            {orderItem.Status}
          </span>
        </div>

        <div className="order-view__hero">
          <div className="order-view__hero-icon" aria-hidden="true">
            <FaReceipt />
          </div>
          <div className="order-view__hero-text">
            <span className="order-view__eyebrow">
              <Translate value="order_receipt_official" />
            </span>
            <h1 className="order-view__title">{receiptNumber}</h1>
            <p className="order-view__subtitle">
              {orderItem.CreatedAt} · ${orderItem.Total} USD · {orderItem.Kind}
            </p>
          </div>
        </div>
      </header>
    );
  }

  render() {
    const { loading, error, orderItem } = this.state;

    if (loading) {
      return (
        <div className="order-view order-view--centered">
          <MiniLoader />
        </div>
      );
    }

    if (error || !orderItem?.Id) {
      return (
        <div className="order-view order-view--centered">
          <div className="order-view__error-card">
            <FaReceipt className="order-view__error-icon" />
            <p>
              <Translate value={error || "try_later"} />
            </p>
            <Link to="/paylist" className="order-view__back-btn">
              <Translate value="pay_orders" />
            </Link>
          </div>
        </div>
      );
    }

    const labels = getInvoiceLabels();

    return (
      <div className="order-view">
        {this.renderHeader(orderItem)}
        <div className="order-view__frame">
          <PDFViewer key={orderItem.Id} className="order-view__viewer">
            <OrderDocument orderItem={orderItem} labels={labels} />
          </PDFViewer>
        </div>
      </div>
    );
  }
}

function mapStateToProps(reducer) {
  return {
    user_id: reducer.session.auth.id,
    token: reducer.session.auth.access_token,
  };
}

const mapDispatchToProps = (dispatch) => ({
  LoadItem: (token, params) =>
    dispatch(actions.loadItem({ token, params })),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrderPdf);
